<!-- eslint-disable vue/require-v-for-key -->
<template>
  <div v-if="price.landlordFee || price.depositAmount || additionalCosts.length">
    <div class="b-grey mb-xs"></div>

    <ion-accordion-group class="w-100 bg-white" :value="['cost']">
      <ion-accordion class="w-100 bg-white" value="cost">
        <ion-item slot="header" color="white">
          <ion-label class="font-md bold-medium">{{
            t('additionalCosts.costsAndServices')
          }}</ion-label>
        </ion-item>
        <div slot="content">
          <p class="font-sm mt-sm py-xs">
            {{ t('additionalCosts.description') }}
          </p>

          <div v-if="price.landlordFee">
            <div class="d-flex justify-space-between px-md py-xs ion-align-items-center">
              <div class="d-flex ion-align-items-center">
                <ion-icon slot="start" :icon="bed" size="small" color="grey"></ion-icon>

                <div class="mx-xs">
                  <p class="font-sm bold-normal">{{ t('additionalCosts.bookingPrice.title') }}</p>
                  <p class="text-color-placeholder font-xxs">
                    {{ t('additionalCosts.bookingPrice.description') }}
                  </p>
                </div>
              </div>
              <p class="font-sm bold-normal">
                {{ amountMask(price.landlordFee as number, price.currency as string) }}
              </p>
            </div>
            <div v-if="price.depositAmount || additionalCosts.length" class="b-grey"></div>
          </div>

          <div v-if="price.depositAmount">
            <div class="d-flex justify-space-between px-md py-xs ion-align-items-center">
              <div class="d-flex ion-align-items-center">
                <ion-icon slot="start" :icon="wallet" size="small" color="grey"></ion-icon>

                <div class="mx-xs">
                  <p class="font-sm bold-normal">{{ t('additionalCosts.deposit.title') }}</p>
                  <p class="text-color-placeholder font-xxs">
                    {{ t('additionalCosts.deposit.description') }}
                  </p>
                </div>
              </div>
              <p class="font-xs bold-normal">
                {{ amountMask(price.depositAmount as number, price.currency as string) }}
              </p>
            </div>
          </div>

          <div
            v-if="(price.depositAmount || price.landlordFee) && additionalCosts.length"
            class="b-grey"
          ></div>

          <div v-if="additionalCosts.length" class="mt-lg">
            <p class="text-color-placeholder font-sm bold-medium mb-lg">
              {{ t('additionalCosts.title') }}
            </p>

            // eslint-disable-next-line vue/require-v-for-key
            <div v-for="(cost, idx) in additionalCosts">
              <div class="d-flex justify-space-between px-md py-xs ion-align-items-center">
                <div class="d-flex ion-align-items-center">
                  <ion-icon
                    slot="start"
                    :icon="cost.icon"
                    size="small"
                    :color="cost.color"
                  ></ion-icon>

                  <div class="mx-xs">
                    <p class="font-sm bold-normal">{{ cost.label }}</p>
                    <p
                      v-if="cost.includedModel !== 'included'"
                      class="text-color-placeholder font-xxs"
                    >
                      {{
                        t(
                          `announcement.payment.${price.paymentCycle !== 'Unknown' ? price.paymentCycle : 'monthly'}`,
                        )
                      }}
                    </p>
                  </div>
                </div>
                <p class="font-xs bold-normal">
                  {{
                    cost.includedModel !== 'included'
                      ? amountMask(cost.price as number, price.currency as string)
                      : t('additionalCosts.included')
                  }}
                </p>
              </div>
              <div v-if="idx < additionalCosts.length - 1" class="b-grey"></div>
            </div>
          </div>
        </div>
      </ion-accordion>
    </ion-accordion-group>
  </div>
</template>

<script setup lang="ts">
  import { IonIcon, IonAccordion, IonAccordionGroup, IonItem, IonLabel } from '@ionic/vue';
  import { Cost, Price } from '@/interfaces/elastic/elastic.interface';
  import { useI18n } from 'vue-i18n';
  import { bed, flame, flash, sparkles, wallet, water, wifi } from 'ionicons/icons';
  import { onMounted, ref } from 'vue';
  import { amountMask } from '@/utils/validations/formats';

  const { t } = useI18n();

  const props = withDefaults(
    defineProps<{
      price: Price;
    }>(),
    {},
  );

  const additionalCosts = ref<Cost[]>([]);

  const additionalCostsOptions = ref<Cost[]>([
    {
      value: 'gasFee',
      label: t('additionalCosts.gasFee'),
      price: null,
      includedModel: 'included',
      icon: flame,
      color: 'orange',
    },
    {
      value: 'internetFee',
      label: t('additionalCosts.internetFee'),
      price: null,
      includedModel: 'included',
      icon: wifi,
      color: 'success',
    },
    {
      value: 'waterFee',
      label: t('additionalCosts.waterFee'),
      price: null,
      includedModel: 'included',
      icon: water,
      color: 'info',
    },
    {
      value: 'cleaningFee',
      label: t('additionalCosts.cleaningFee'),
      price: null,
      includedModel: 'included',
      icon: sparkles,
      color: 'primary',
    },
    {
      value: 'electricityFee',
      label: t('additionalCosts.electricityFee'),
      price: null,
      includedModel: 'included',
      icon: flash,
      color: 'accent',
    },
  ]);

  const verifyAdditionalCosts = () => {
    const fees: (keyof Price)[] = [
      'gasFee',
      'electricityFee',
      'cleaningFee',
      'internetFee',
      'waterFee',
    ];

    fees.forEach((fee) => {
      if (props.price[fee] !== undefined && props.price[fee] !== null) {
        const foundOption = additionalCostsOptions.value.find((option) => option.value === fee);

        const copiedOption = { ...foundOption };

        copiedOption.includedModel = props.price[fee] === 0 ? 'included' : 'not included';
        copiedOption.price = props.price[fee];

        additionalCosts.value.push(copiedOption as any);
      }
    });
  };

  onMounted(() => {
    verifyAdditionalCosts();
  });
</script>

<style lang="css" scoped></style>
